import { StacksBySite } from '@pubstack/common/src/stack'
import { FunctionComponent, ReactNode, useState } from 'react'
import Button from '~/components/Button'
import { Flyout } from '~/components/Flyout'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { StackUsage } from '~/modules/adm/StackUsage'
import { StackUsageDetails } from '~/modules/adm/StackUsageDetails'

export type RuleDetail = { label: string; value: ReactNode }

type Props = {
  /** The rule's name, which titles the flyout. */
  name: string
  /** The rule's own settings, in the order the table shows its columns. */
  details: RuleDetail[]
  /** Which stacks run this rule, keyed by media name — the same shape a bidder rule's `usedInStacks` has. */
  stacksUse?: StacksBySite
  onEdit?: () => void
  /** Absent on a Pubstack-owned rule, which is what replaces the Edit button with the reason it is missing. */
  editDisabledReason?: string
  editTrackingLabel: TrackingLabel
}

/**
 * What a lazy loading or refresh rule is, and where it is running — the read-only view both rule lists
 * lacked, where the only way to see a rule's settings was to open the form that changes them.
 *
 * Modelled on `ViewBidderRuleFlyout`, deliberately: a rule is a rule, and a publisher who has read one of
 * these should not have to learn a second layout. Same title-and-details shape, same "Used by" section,
 * same Edit in the footer.
 *
 * The settings themselves are passed in rather than derived here. Lazy loading counts viewports and refresh
 * counts seconds; the two have no field in common beyond the name, and a component that tried to know both
 * would be a switch on which rule it was handed.
 */
export const RuleDetailsFlyout: FunctionComponent<Props> = ({ name, details, stacksUse, onEdit, editDisabledReason, editTrackingLabel }) => {
  const [showUsage, setShowUsage] = useState(false)
  if (showUsage) return <StackUsageDetails name={name} stacksUse={stacksUse} onBack={() => setShowUsage(false)} />

  return (
    <Flyout.Content>
      <Flyout.Title>{name} - details</Flyout.Title>
      <Flyout.Body>
        <div className={'flex flex-col gap-6'}>
          <section className={'flex flex-col gap-2'}>
            <h3 className={'m-0 font-medium'}>Settings</h3>
            <dl className={'m-0 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2'}>
              {details.map(({ label, value }) => (
                <div key={label} className={'col-span-2 grid grid-cols-subgrid'}>
                  <dt className={'text-slate-grey'}>{label}</dt>
                  <dd className={'m-0'}>{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <StackUsage stacksUse={stacksUse} onSeeAll={() => setShowUsage(true)} />
        </div>
      </Flyout.Body>
      <Flyout.Actions>
        {onEdit ? (
          <Button variant={'secondary'} iconName={'edit'} onClick={onEdit} trackingLabel={editTrackingLabel}>
            Edit rule
          </Button>
        ) : (
          /* Said rather than shown disabled: a greyed button invites a hover to find out why, and the reason
             is one short sentence. */
          <span className={'text-slate-grey'}>{editDisabledReason}</span>
        )}
      </Flyout.Actions>
    </Flyout.Content>
  )
}
